import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation }  from 'react-i18next'
import { useAuth }         from '@/context/AuthContext'
import { login as apiLogin, getMe, getAuthProviders, googleLogin } from '@/api/auth'
import { setLocale, SUPPORTED_LOCALES } from '@/i18n/i18n'
import { startTelegramOidcLogin } from '@/utils/telegramOidc'
import GoogleSignInButton from '@/components/GoogleSignInButton'

const LOCALE_FLAGS  = { uz: '🇺🇿', ru: '🇷🇺', en: '🇬🇧' }
const ALLOWED_ROLES = ['admin', 'teacher']
const OIDC_CLIENT_ID = import.meta.env.VITE_TELEGRAM_OIDC_CLIENT_ID || ''

export default function LoginPage() {
  const { t, i18n } = useTranslation()
  const { login }   = useAuth()
  const navigate    = useNavigate()
  const location    = useLocation()

  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error,    setError]    = useState(
    location.state?.forbidden ? t('auth.accessDenied', 'Bu panelga kirish huquqingiz yo\'q.') : null
  )
  const [loading,  setLoading]  = useState(false)
  const [providers, setProviders] = useState(null)

  // Surface an error stashed by the Telegram OIDC callback gate (App.jsx).
  // Done in an effect, not the useState initializer, so React StrictMode's
  // double-invoke in dev doesn't consume the sessionStorage flag before the
  // "real" render reads it.
  useEffect(() => {
    const oidcError = sessionStorage.getItem('tg_oidc_error')
    if (oidcError) {
      sessionStorage.removeItem('tg_oidc_error')
      setError(oidcError)
    }
  }, [])

  // Discover which social logins the backend is configured for.
  useEffect(() => {
    const controller = new AbortController()
    getAuthProviders({ signal: controller.signal })
      .then(({ data }) => setProviders(data))
      .catch(() => { /* leave social buttons hidden on failure */ })
    return () => controller.abort()
  }, [])

  function handleTelegramOidcLogin() {
    startTelegramOidcLogin(OIDC_CLIENT_ID, `${window.location.origin}/`)
  }

  async function handleGoogleCredential(idToken) {
    setLoading(true); setError(null)
    try {
      const { data } = await googleLogin(idToken)
      // Same gate as password login — the admin panel is admin/teacher only.
      if (!data.user.is_superuser && !ALLOWED_ROLES.includes(data.user.role)) {
        setError(t('auth.accessDenied', 'Bu panelga kirish huquqingiz yo\'q.'))
        return
      }
      login({ access: data.access, refresh: data.refresh, user: data.user })
      navigate('/dashboard', { replace: true })
    } catch (err) {
      setError(err.response?.data?.detail ?? t('auth.loginFailed'))
    } finally {
      setLoading(false)
    }
  }

  async function handleSubmit(e) {
    e.preventDefault()
    if (!username || !password) return
    setLoading(true); setError(null)
    try {
      const { data } = await apiLogin(username, password)
      login({ access: data.access, refresh: data.refresh, user: null })
      const meRes = await getMe()
      const user = meRes.data
      if (!user.is_superuser && !ALLOWED_ROLES.includes(user.role)) {
        setError(t('auth.accessDenied', 'Bu panelga kirish huquqingiz yo\'q.'))
        return
      }
      login({ access: data.access, refresh: data.refresh, user })
      navigate('/dashboard', { replace: true })
    } catch (err) {
      setError(err.response?.data?.detail ?? t('auth.loginFailed'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4"
      style={{ background: 'var(--bg)' }}>

      {/* Lang switcher */}
      <div className="absolute top-4 right-4 flex gap-1.5">
        {SUPPORTED_LOCALES.map((lng) => (
          <button
            key={lng}
            onClick={() => setLocale(lng)}
            className="px-2.5 py-1 rounded-lg text-xs font-semibold transition-colors"
            style={{
              background: i18n.language === lng ? 'var(--coral)' : 'var(--surface)',
              color:      i18n.language === lng ? '#fff'          : 'var(--ink-2)',
              border:     `1px solid var(--border)`,
            }}
          >
            {LOCALE_FLAGS[lng]} {lng.toUpperCase()}
          </button>
        ))}
      </div>

      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-white font-black text-2xl mx-auto mb-3"
            style={{ background: 'var(--coral)' }}>
            5m
          </div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--ink)' }}>5mi.uz</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--ink-3)' }}>Admin Panel</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 rounded-2xl p-6"
          style={{ background: 'var(--surface)', border: '1px solid var(--border)', boxShadow: 'var(--shadow-md)' }}>

          <div className="space-y-1">
            <label className="block text-sm font-medium" style={{ color: 'var(--ink-2)' }}>
              {t('auth.username')}
            </label>
            <input
              type="text"
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              className="field-base"
            />
          </div>

          <div className="space-y-1">
            <label className="block text-sm font-medium" style={{ color: 'var(--ink-2)' }}>
              {t('auth.password')}
            </label>
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="field-base"
            />
          </div>

          {error && (
            <p className="text-xs px-3 py-2 rounded-lg"
              style={{ color: 'var(--danger)', background: 'var(--danger-tint)', border: '1px solid var(--danger-soft,#fca5a5)' }}>
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-2.5 text-white font-semibold rounded-lg transition-opacity mt-2 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: 'var(--coral)' }}
          >
            {loading && (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            )}
            {t('auth.login')}
          </button>
        </form>

        {(OIDC_CLIENT_ID || providers?.google?.enabled) && (
          <div className="mt-4 rounded-2xl p-5 flex flex-col items-center gap-3"
            style={{ background: 'var(--surface)', border: '1px solid var(--border)', boxShadow: 'var(--shadow-md)' }}>
            <p className="text-xs font-medium" style={{ color: 'var(--ink-3)' }}>yoki</p>
            {providers?.google?.enabled && (
              <div className="w-full">
                <GoogleSignInButton
                  clientId={providers.google.client_id}
                  onCredential={handleGoogleCredential}
                  onError={() => setError(t('auth.googleLoadFailed', "Google login'ni yuklab bo'lmadi"))}
                />
              </div>
            )}
            {OIDC_CLIENT_ID && (
              <button
                type="button"
                onClick={handleTelegramOidcLogin}
                disabled={loading}
                className="w-full py-2.5 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: '#2ba5e0', color: '#fff' }}
              >
                <svg width="16" height="16" viewBox="0 0 240 240" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M240 120c0 66.3-53.7 120-120 120S0 186.3 0 120 53.7 0 120 0s120 53.7 120 120z" fill="#fff" fillOpacity="0"/>
                  <path d="M109.2 175.6c-4.1 0-3.4-1.5-4.8-5.4l-12-39.7 92.4-54.9" fill="#c8daea"/>
                  <path d="M92.4 130.5l67.7-60.4c3.1-2.8 6.9-.5 4.9 3.9l-27.4 101.9c-2.6 5.1-6.3 4.9-9.5 2.5l-27.7-20.4-13.4 12.9c-1.5 1.5-2.9 2.9-5.1 2.9" fill="#a9c9dd"/>
                </svg>
                Telegram orqali kirish
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
